import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { finalize, flatMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LoaderService } from 'src/app/services/loader.service';
import { OpfoelgningService, ServiceAftaleService, TilvalgService } from 'src/app/api/services';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSelectionListChange } from '@angular/material/list';
import { MatDialog } from '@angular/material/dialog';
import { OpfoelgningDialogResponse, OpfoelgningAction, FastOrdreNoteDialogData } from '../models';
import { LogicService } from 'src/app/services/logic.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ReactivateServiceaftaleRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/reactivate-serviceaftale-request';
import { GetServiceaftaleResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceaftale-response';
import { OpgaveEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/opgave-enum';
import { OrdreService } from 'src/app/services/ordre.service';
import { AuthService } from 'src/app/services/auth.service';
import { EnvironmentLoaderService } from 'src/app/services/environment-loader.service';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { AuditHistoryType } from './models/selectfiltermodels';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { TilvalgGroupDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/tilvalg-group-dto';
import { FilterOption } from '../../shared/history/select-filter/select-filter.component';
import { FastOrdreNoteEditDialogComponent } from './fast-ordre-note-edit-dialog/fast-ordre-note-edit-dialog.component';
import { AuditHistoryEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/audit-history-enum';
import { ServiceaftaleStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceaftale-state-enum';
import { ServiceAgreementStateService } from 'src/app/services/serviceAgreementState.service';

@Component({
  selector: 'app-details',
  templateUrl: './aftale-details.component.html',
  styleUrls: ['./aftale-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AftaleDetailsComponent implements OnInit, OnDestroy {
  @Output() navigateMenu: EventEmitter<string> = new EventEmitter();

  auditHistoryTypesKeys = Object.keys(AuditHistoryType)
    .filter(Number)
    .map(([id]) => new FilterOption(parseInt(id), AuditHistoryType[id], parseInt(id) === AuditHistoryType.Alt));

  serviceaftale: ServiceaftaleDto;
  okKundeAftaleNr: number;

  tilvalgGroups: TilvalgGroupDto[];

  unsubscribe$ = new Subject<void>();
  isLoading = true;
  page: string;
  isAdmin = false;
  isGDPR = false;
  canWrite = false;

  canCreateEftersyn: boolean;
  canReactivate: boolean = false;
  showSlutopgoerelser: boolean = false;

  updatedAftale: ServiceaftaleDto;

  selectedAuditHistoryType: AuditHistoryType = AuditHistoryType.Alt;

  serviceaftaleStateEnum: typeof ServiceaftaleStateEnum = ServiceaftaleStateEnum;

  readonly entityType: AuditHistoryEnum = AuditHistoryEnum.Serviceaftale;
  errorMessage: string | number;

  naervarmeUrl: string;

  constructor(
    private route: ActivatedRoute,
    private serviceaftaleService: ServiceAftaleService,
    private loader: LoaderService,
    private router: Router,
    private errorHandler: ErrorHandlerService,
    public dialog: MatDialog,
    private logic: LogicService,
    private signalR: SignalrService,
    private ordreService: OrdreService,
    private auth: AuthService,
    private env: EnvironmentLoaderService,
    private notification: NotificationService,
    private opfoelgningService: OpfoelgningService,
    private tilvalgService: TilvalgService,
    private serviceAftaleStateService: ServiceAgreementStateService,
  ) {
    this.page = this.route.snapshot.fragment ? this.route.snapshot.fragment : 'Abonnement';
    this.isAdmin = this.auth.isAdministrator;
    this.isGDPR = this.auth.isGDPR;
    this.canWrite = this.auth.canWrite;
    this.canReactivate = this.auth.isAdministrator || this.auth.isKundeservice || this.auth.isSuperuser;
    this.showSlutopgoerelser = this.auth.isAdministrator || this.auth.isKundeservice || this.auth.isSuperuser || this.auth.isSalg;
    this.naervarmeUrl = this.env.config.naervarmePortalUrl;
  }

  ngOnInit(): void {
    this.router.events.subscribe(() => {
      this.page = this.route.snapshot.fragment ? this.route.snapshot.fragment : 'Abonnement';
    });

    const okKundeAftaleNr = Number(this.route.snapshot.paramMap.get('okKundeAftaleNr'));
    this.okKundeAftaleNr = okKundeAftaleNr;

    this.fetchAftale();

    this.signalR.serviceaftaleChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe((serviceaftale: ServiceaftaleDto) => {
      let debug = 'serviceaftaleChanged aftaleNo=' + serviceaftale.okKundeAftaleNr;
      const username = this.auth.username;
      // Can be used for debugging signal R
      //debug = debug + ', user=' + username?.toLowerCase() + ', modifiedBy=' + serviceaftale.modifiedBy.toLowerCase();
      if (this.serviceaftale.id == serviceaftale.id) {
        if (serviceaftale.modifiedBy.toLowerCase() == username?.toLowerCase()) {
          this.serviceaftale = serviceaftale;
        } else {
          if (this.env.isFeatureEnabled('concurrencyFeature')) {
            this.updatedAftale = serviceaftale;
          }
        }
      }
      console.log(debug, serviceaftale);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  fetchAftale() {
    this.loader.show();

    this.serviceaftaleService
      .getServiceAftaleForKunde({
        okKundeAftaleNr: this.okKundeAftaleNr,
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(
        flatMap((response: GetServiceaftaleResponse) => {
          this.serviceaftale = response.serviceaftale;
          this.canCreateEftersyn = this.logic.canCreateEftersyn(this.serviceaftale);
          this.isLoading = false;
          if (response.errorCode && response.errorCode === 900001) {
            this.errorMessage = response.errorMessage || response.errorCode;
            this.errorHandler.showG4Error();
          }
          return this.tilvalgService.getTilvalgGroupsForServiceabonnement({
            caller: EGIS_SERVICE_CALLER,
            serviceAbonnementId: response.serviceaftale.serviceAbonnement?.id,
          });
        }),
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (response: TilvalgGroupDto[]) => {
          this.tilvalgGroups = response;
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  createTilkald() {
    this.ordreService.create(OpgaveEnum.Tilkald, this.serviceaftale, null).subscribe(
      (serviceordre: ServiceordreDto) => {},
      (error) => {
        this.errorHandler.HandleError(error);
      },
    );
  }

  createOpstart() {
    this.ordreService.create(OpgaveEnum.Opstart, this.serviceaftale, null).subscribe(
      (serviceordre: ServiceordreDto) => {},
      (error) => {
        this.errorHandler.HandleError(error);
      },
    );
  }

  createEftersyn() {
    this.ordreService.create(OpgaveEnum.Eftersyn, this.serviceaftale, null).subscribe(
      (serviceordre: ServiceordreDto) => {},
      (error) => {
        this.errorHandler.HandleError(error);
      },
    );
  }

  getProductIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.productText
      ? serviceAftale.serviceAbonnement.productText.toLowerCase()
      : '';
  }

  selectionChanged(change: MatSelectionListChange) {
    this.router.navigate(['aftale', this.serviceaftale.okKundeAftaleNr], { fragment: change.options[0].value });
    this.page = change.options[0].value;
  }

  openFastOrdreNoteDialog() {
    const data: FastOrdreNoteDialogData = {
      serviceaftale: this.serviceaftale,
    };

    const dialogRef = this.dialog.open(FastOrdreNoteEditDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data,
    });

    dialogRef.afterClosed().subscribe((result: ServiceaftaleDto) => {
      if (result) {
        this.serviceaftale = result;
        this.notification.success('Fast ordre note er gemt.');
      }
    });
  }

  anonymise() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Er du sikker?',
        body: 'Er du sikker på at du vil anonymisere serviceaftalen?',
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.loader.show();
        this.serviceaftaleService
          .anonymise({
            caller: EGIS_SERVICE_CALLER,
            serviceaftaleId: this.serviceaftale.id,
          })
          .pipe(
            finalize(() => {
              this.loader.hide();
            }),
            takeUntil(this.unsubscribe$),
          )
          .subscribe(
            () => {
              this.fetchAftale();
            },
            (error) => {
              this.errorHandler.HandleError(error);
            },
          );
      }
    });
  }

  canServiceaftaleBeReactivated(): boolean {
    return this.serviceAftaleStateService.canServiceaftaleBeReactivated(this.serviceaftale);
  }

  reactivateServiceAgreement() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Er du sikker?',
        body: 'Er du sikker på at du vil genaktivere serviceaftalen?',
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        const request: ReactivateServiceaftaleRequest = {
          serviceaftaleNo: this.serviceaftale.serviceAftaleNo,
          rowVersion: this.serviceaftale.rowVersion,
        };

        this.loader.show();
        this.serviceaftaleService
          .reactivate({
            caller: EGIS_SERVICE_CALLER,
            body: request,
          })
          .pipe(
            finalize(() => {
              this.loader.hide();
            }),
            takeUntil(this.unsubscribe$),
          )
          .subscribe(
            (serviceaftale: ServiceaftaleDto) => {
              this.serviceaftale = serviceaftale;
            },
            (error) => {
              this.errorHandler.HandleError(error);
            },
          );
      }
    });
  }

  onServiceAftaleChange(serviceAftale: ServiceaftaleDto) {
    this.serviceaftale = serviceAftale;
  }

  refresh() {
    this.serviceaftale = this.updatedAftale;
    this.updatedAftale = null;
  }

  onOpfoelgningChanged(response: OpfoelgningDialogResponse) {
    if (response.action == OpfoelgningAction.Upsert) {
      this.loader.show();

      this.opfoelgningService
        .upsertServiceaftaleOpfoelgning({
          caller: EGIS_SERVICE_CALLER,
          body: {
            id: response.opfoelgning.id,
            note: response.opfoelgning.note,
            deadline: response.opfoelgning.deadline ? new Date(response.opfoelgning.deadline).toOkDateString() : null,
            prioritet: response.opfoelgning.priority,
            entityNo: response.opfoelgning.entityNo,
          },
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er gemt.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else if (response.action == OpfoelgningAction.Delete) {
      this.opfoelgningService
        .removeServiceaftaleOpfoelgning({ caller: EGIS_SERVICE_CALLER, id: response.opfoelgning.id })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er fjernet.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    }
  }

  openNaervarme() {
    window.open(this.naervarmeUrl + '/aftale/' + this.serviceaftale.okKundeAftaleNr, 'NaervarmeWindow');
  }
}
